import * as React from 'react'

import { action, makeObservable } from 'mobx'
import { inject, observer } from 'mobx-react'
import { classList } from 'react-classlist-helper'

import ViewModes from '~/client/src/desktop/enums/ViewModes'
import DesktopInitialState from '~/client/src/desktop/stores/DesktopInitialState'
import { CalendarIcon } from '~/client/src/shared/components/icons/CalendarIcon'
import { GanttIcon } from '~/client/src/shared/components/icons/GanttIcon'
import { ListIcon } from '~/client/src/shared/components/icons/ListIcon'
import { MapIcon } from '~/client/src/shared/components/icons/MapIcon'
import TooltipWrapper from '~/client/src/shared/components/TooltipWrapper/TooltipWrapper'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import WorkflowDrawingGuardStore from '~/client/src/shared/stores/ui/WorkflowDrawingGuard.store'

// localization: translated

interface IViewModeToggle {
  state?: DesktopInitialState
  viewState: any
  viewModes: IModeSettings[]
  setViewMode?: (mode: ViewModes) => void
  workflowDrawingGuardStore?: WorkflowDrawingGuardStore
}

export interface IModeSettings {
  viewMode: ViewModes
  getTooltipText: () => string
  itemStyle: string
  isDisabled?: boolean
}

const VIEW_MODE_ICON_COMPONENTS: Record<
  string,
  React.FC<{ size?: number | string; className?: string }>
> = {
  [ViewModes.Gantt]: GanttIcon,
  [ViewModes.Calendar]: CalendarIcon,
  [ViewModes.List]: ListIcon,
  [ViewModes.Map]: MapIcon,
}

export const getModeSettings = (mode: ViewModes, isDisabled?: boolean) => {
  switch (mode) {
    case ViewModes.Calendar:
      return {
        viewMode: ViewModes.Calendar,
        getTooltipText: () => Localization.translator.calendarView,
        itemStyle: 'views-toggle-calendar',
        isDisabled,
      }

    case ViewModes.Gantt:
      return {
        viewMode: ViewModes.Gantt,
        getTooltipText: () => Localization.translator.ganttView,
        itemStyle: 'views-toggle-gantt',
        isDisabled,
      }

    case ViewModes.List:
      return {
        viewMode: ViewModes.List,
        getTooltipText: () => Localization.translator.listView,
        itemStyle: 'views-toggle-list',
        isDisabled,
      }

    case ViewModes.Map:
      return {
        viewMode: ViewModes.Map,
        getTooltipText: () => Localization.translator.mapView,
        itemStyle: 'views-toggle-map',
        isDisabled,
      }
  }
}

/**
 * @deprecated Use ViewModeToggleGroup from
 * `~/client/src/desktop/components/ViewModeToggleGroup/ViewModeToggleGroup`
 * — a functional shadcn ToggleGroup with Lucide icons. This class component
 * remains only for views not yet migrated off it.
 */
@inject('workflowDrawingGuardStore')
@observer
export default class ViewModeToggle extends React.Component<IViewModeToggle> {
  public constructor(props: IViewModeToggle) {
    super(props)
    makeObservable(this)
  }

  private get currentViewMode() {
    return this.props.viewState.viewMode
  }

  public render() {
    return (
      <div className="tw:inline-flex tw:bg-background tw:!ml-4 tw:h-10 tw:z-[1] tw:grow-0">
        {this.props.viewModes.map((item, index) =>
          this.renderMenuItem(item, index),
        )}
      </div>
    )
  }

  private renderMenuItem(
    { getTooltipText, viewMode, isDisabled }: IModeSettings,
    index: number,
  ) {
    const isActive = this.currentViewMode === viewMode
    const IconComponent = VIEW_MODE_ICON_COMPONENTS[viewMode]

    return (
      <div className="tw:[&_*]:!outline-none" key={viewMode}>
        <TooltipWrapper content={getTooltipText()}>
          <div
            className={classList({
              'tw:flex tw:items-center tw:justify-center tw:cursor-pointer tw:size-10 tw:border tw:border-solid tw:border-border tw:hover:border-primary': true,
              'tw:bg-background': !isActive,
              'tw:pointer-events-none tw:border-primary tw:bg-primary/15':
                isActive,
              'tw:pointer-events-none tw:opacity-50': isDisabled,
              'tw:rounded-l': index === 0,
              'tw:rounded-r': index === this.props.viewModes.length - 1,
            })}
            onClick={this.setViewMode.bind(this, viewMode, isDisabled)}
            data-testid={`view-mode-toggle-${viewMode}`}
          >
            {IconComponent && (
              <IconComponent
                className={classList({
                  'tw:text-muted-foreground': !isActive && !isDisabled,
                  'tw:text-primary': isActive,
                })}
              />
            )}
          </div>
        </TooltipWrapper>
      </div>
    )
  }

  @action.bound
  private setViewMode(mode: ViewModes, isDisabled?: boolean) {
    if (isDisabled) {
      return
    }

    if (this.currentViewMode !== mode) {
      // Leaving Map mode unmounts the map — confirm first if a map-first
      // drawing session would lose markups (no-op when no session is active).
      this.props.workflowDrawingGuardStore.guard(
        action(() => {
          this.props.viewState.viewMode = mode
          this.props.setViewMode?.(mode)
        }),
      )
    }
  }
}
